import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    propertyType: '',
    propertySize: '',
    services: [] as string[],
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('ownerName', formData.ownerName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('address', formData.address);
      formBody.append('propertyType', formData.propertyType);
      formBody.append('propertySize', formData.propertySize);
      formBody.append('services', formData.services.join(', '));
      formBody.append('description', formData.description);

      const response = await fetch('https://readdy.ai/api/form/d6g7lcqau1b90a404cfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ ownerName: '', email: '', phone: '', address: '', propertyType: '', propertySize: '', services: [], description: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${scrolled ? 'bg-black border-b border-[#C9A961]' : 'bg-black/95 backdrop-blur-md'}`} style={{ height: '80px' }}>
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px] h-full flex items-center justify-between">
          <div className="flex items-center">
            <img src="https://static.readdy.ai/image/b8716fa8a6d7792031f552f74d12f7bc/83934bc45ac34654febf39565a6f5f5c.png" alt="Elite Property Operations" className="h-16 md:h-20 lg:h-24 w-auto" />
          </div>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-12">
            <a href="#services" className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 relative group">
              Services
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C9A961] group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#programs" className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 relative group">
              Contracting
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C9A961] group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#why-us" className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 relative group">
              Why Us
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C9A961] group-hover:w-full transition-all duration-300"></span>
            </a>
            <a href="#regions" className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 relative group">
              Regions
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C9A961] group-hover:w-full transition-all duration-300"></span>
            </a>
            <Link to="/contact" className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors duration-300 relative group">
              Contact
              <span className="absolute bottom-[-4px] left-0 w-0 h-[2px] bg-[#C9A961] group-hover:w-full transition-all duration-300"></span>
            </Link>
          </div>
          <Link to="/contact" className="hidden lg:flex h-[44px] px-8 border border-[#C9A961] bg-transparent text-[#F8F8F8] text-[14px] font-medium items-center justify-center rounded-sm hover:bg-[#C9A961] hover:text-black transition-all duration-400 whitespace-nowrap cursor-pointer">
            Schedule Assessment
          </Link>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#F8F8F8] cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-[28px]`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-black border-t border-[#2C2C2C] px-6 py-6 flex flex-col gap-5">
            <a href="#services" onClick={() => setMenuOpen(false)} className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors">Services</a>
            <a href="#programs" onClick={() => setMenuOpen(false)} className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors">Contracting</a>
            <a href="#why-us" onClick={() => setMenuOpen(false)} className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors">Why Us</a>
            <a href="#regions" onClick={() => setMenuOpen(false)} className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors">Regions</a>
            <Link to="/contact" onClick={() => setMenuOpen(false)} className="text-[13px] uppercase tracking-[1.2px] text-[#6B6B6B] hover:text-[#C9A961] transition-colors">Contact</Link>
            <Link to="/contact" onClick={() => setMenuOpen(false)} className="h-[44px] px-6 border border-[#C9A961] bg-transparent text-[#F8F8F8] text-[13px] font-medium flex items-center justify-center rounded-sm hover:bg-[#C9A961] hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer w-fit">
              Schedule Assessment
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" style={{ marginTop: '80px' }}>
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a5231a16a74e26b60c532ddb428f5995.jpg"
            alt="Luxury Estate"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px] w-full py-20 md:py-0">
          <div className="max-w-full md:max-w-[600px]">
            <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] mb-6 font-medium">ESTABLISHED EXCELLENCE</p>
            <h1 className="text-[42px] md:text-[56px] lg:text-[72px] leading-[1.1] text-[#F8F8F8] mb-6 md:mb-8 font-light">
              Elite Property <span className="font-normal">Operations</span>
            </h1>
            <h2 className="text-[20px] md:text-[24px] lg:text-[28px] leading-[1.4] text-[#F8F8F8] font-light mb-6 md:mb-8">
              Operational Excellence for Exceptional Properties
            </h2>
            <p className="text-[15px] md:text-[16px] text-[#B8B8B8] leading-[1.6] mb-10 md:mb-14">
              Structured Property Systems. Elevated Standards. Proven Performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="h-[52px] px-6 md:px-8 bg-black text-[#F8F8F8] text-[14px] md:text-[15px] font-medium flex items-center justify-center rounded-sm hover:shadow-[0_8px_24px_rgba(201,169,97,0.15)] transition-all duration-300 whitespace-nowrap cursor-pointer">
                Schedule Property Assessment
              </Link>
              <Link to="/contact" className="h-[52px] px-6 md:px-8 border border-[#C9A961] bg-transparent text-[#C9A961] text-[14px] md:text-[15px] font-medium flex items-center justify-center rounded-sm hover:shadow-[0_8px_24px_rgba(201,169,97,0.15)] transition-all duration-300 whitespace-nowrap cursor-pointer">
                Request Commercial Proposal
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Company Positioning */}
      <section className="bg-[#F8F8F8] py-[80px] md:py-[120px] lg:py-[140px]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px]">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-[5%] relative">
            <div className="hidden lg:block absolute left-[40%] top-0 bottom-0 w-[1px] bg-[#C9A961]"></div>
            <div className="w-full lg:w-[40%]">
              <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] mb-6">COMPANY POSITIONING</p>
              <h2 className="text-[32px] md:text-[40px] lg:text-[48px] leading-[1.2] text-black font-light">
                A Structured Approach to Property Operations
              </h2>
            </div>
            <div className="w-full lg:w-[55%]">
              <div className="w-full h-[1px] bg-[#C9A961] mb-8 lg:hidden"></div>
              <p className="text-[16px] md:text-[18px] leading-[1.8] text-[#2C2C2C] font-light">
                <span className="font-normal">Elite Property Operations™ delivers disciplined, systemized maintenance and asset preservation</span> for residential estates, commercial properties, HOAs, and institutional facilities. We operate with precision and operational oversight to ensure long-term performance, presentation, and asset protection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section id="services" className="bg-[#0A0A0A] py-[80px] md:py-[120px] lg:py-[160px]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px]">
          <div className="text-center mb-12 md:mb-20">
            <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] mb-4">CORE SERVICES</p>
            <h2 className="text-[32px] md:text-[44px] lg:text-[56px] text-[#F8F8F8] font-light">Comprehensive Property Operations</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {[
              {
                title: 'Residential Estate Operations',
                services: ['Interior & Exterior Window Cleaning', 'Landscape Management', 'Exterior Power Washing', 'Paint & Surface Restoration', 'Roof & Gutter Maintenance', 'Irrigation Oversight']
              },
              {
                title: 'Commercial Property Operations',
                services: ['Retail & Plaza Maintenance', 'High-Rise Window Systems', 'Parking Structure Cleaning', 'Building Pressure Washing', 'Facade & Signage Cleaning']
              },
              {
                title: 'HOA & Community Operations',
                services: ['Common Area Maintenance', 'Perimeter Landscaping', 'Clubhouse Exterior Care', 'Quarterly Reporting', 'Emergency Response']
              },
              {
                title: 'Institutional & Municipal Services',
                services: ['Public Facility Maintenance', 'Government Oversight', 'Multi-Property Portfolio Management', 'Long-Term Contracts']
              }
            ].map((category, idx) => (
              <div key={idx} className="bg-[#2C2C2C] rounded-sm p-8 md:p-12 hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)] hover:-translate-y-2 transition-all duration-300 cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className="ri-building-line text-[40px] md:text-[48px] text-[#C9A961]"></i>
                </div>
                <h3 className="text-[20px] md:text-[24px] text-[#F8F8F8] font-semibold mb-6">{category.title}</h3>
                <ul className="space-y-4">
                  {category.services.map((service, sidx) => (
                    <li key={sidx} className="flex items-start gap-4 text-[14px] md:text-[15px] text-[#B8B8B8] hover:text-[#C9A961] hover:translate-x-1 transition-all duration-300 cursor-pointer">
                      <span className="w-[6px] h-[6px] rounded-full bg-[#C9A961] mt-2 flex-shrink-0"></span>
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* General Contracting */}
      <section id="programs" className="bg-[#F8F8F8] py-[80px] md:py-[120px] lg:py-[140px]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px]">
          <div className="w-16 h-[2px] bg-[#C9A961] mb-12"></div>

          <div className="flex flex-col lg:flex-row gap-10 lg:gap-[6%] items-start">
            {/* Left Column */}
            <div className="w-full lg:w-[45%]">
              <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] mb-5">GENERAL CONTRACTING</p>
              <h2 className="text-[30px] md:text-[40px] lg:text-[48px] leading-[1.15] text-black font-light mb-8">
                Construction &amp; General Contracting
              </h2>
              <p className="text-[15px] md:text-[16px] leading-[1.85] text-[#2C2C2C] mb-6">
                Elite Property Operations™ extends beyond structured maintenance and asset preservation. We provide full-service general contracting and construction oversight for residential estates and select commercial projects.
              </p>
              <p className="text-[15px] md:text-[16px] leading-[1.85] text-[#2C2C2C] mb-6">
                Whether you are planning a swimming pool installation, property addition, structural renovation, exterior upgrade, or large-scale construction project, our team delivers disciplined project management and precision execution from concept to completion.
              </p>
              <p className="text-[15px] md:text-[16px] leading-[1.85] text-[#2C2C2C] mb-10">
                We operate with the same structured oversight and quality control standards that define our property operations division ensuring every build enhances long-term value, performance, and presentation.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 h-[52px] px-8 bg-black text-[#F8F8F8] text-[13px] md:text-[14px] uppercase tracking-[1px] font-medium rounded-sm hover:bg-[#C9A961] hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Request Construction Consultation
                <i className="ri-arrow-right-line text-[16px]"></i>
              </Link>
            </div>

            {/* Right Column */}
            <div className="w-full lg:w-[49%] flex flex-col gap-8">
              <div className="w-full h-[220px] md:h-[280px] overflow-hidden rounded-sm">
                <img
                  src="https://readdy.ai/api/search-image?query=Ultra%20luxury%20residential%20estate%20construction%20site%20with%20modern%20architectural%20design%20premium%20swimming%20pool%20installation%20high%20end%20home%20addition%20structural%20renovation%20pristine%20clean%20construction%20site%20professional%20project%20management%20luxury%20real%20estate%20development%20contemporary%20architecture%20neutral%20tones&width=800&height=500&seq=contracting001&orientation=landscape"
                  alt="Elite General Contracting"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-water-flash-line', title: 'Custom Swimming Pool Construction' },
                  { icon: 'ri-building-2-line', title: 'Home Additions & Structural Expansions' },
                  { icon: 'ri-home-gear-line', title: 'Exterior Renovations & Enhancements' },
                  { icon: 'ri-landscape-line', title: 'Outdoor Living Developments' },
                  { icon: 'ri-hammer-line', title: 'Major Remodeling Projects' },
                  { icon: 'ri-clipboard-line', title: 'Project Management & Construction Oversight' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white border border-[#E0E0E0] rounded-sm p-5 flex items-start gap-4 hover:border-[#C9A961] hover:shadow-[0_4px_20px_rgba(201,169,97,0.1)] transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-[22px] text-[#C9A961]`}></i>
                    </div>
                    <span className="text-[13px] md:text-[14px] text-[#2C2C2C] font-medium leading-[1.5]">{item.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full h-[1px] bg-[#E0E0E0] mt-16"></div>
          <p className="text-center text-[13px] md:text-[14px] text-[#6B6B6B] italic mt-6">
            Custom contracts available for commercial and municipal clients.
          </p>
        </div>
      </section>

      {/* Additional Services */}
      <section className="bg-[#2C2C2C] py-[80px] md:py-[120px]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px]">
          <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] text-center mb-12">ADDITIONAL SERVICES</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-file-list-3-line', title: 'Property Condition Audits', desc: 'Comprehensive assessment and documentation' },
              { icon: 'ri-shield-check-line', title: 'Preventative Asset Preservation', desc: 'Long-term protection strategies' },
              { icon: 'ri-sun-line', title: 'Seasonal Preparation', desc: 'Storm, fire, and heat readiness' },
              { icon: 'ri-lightbulb-line', title: 'Exterior Lighting Maintenance', desc: 'Professional illumination care' },
              { icon: 'ri-camera-line', title: 'Security Camera Cleaning', desc: 'Clear surveillance systems' },
              { icon: 'ri-road-map-line', title: 'Hardscape & Driveway Sealing', desc: 'Surface protection and enhancement' },
              { icon: 'ri-team-line', title: 'Vendor Coordination', desc: 'Seamless third-party management' },
              { icon: 'ri-tools-line', title: 'Renovation Oversight', desc: 'Project supervision and quality control' }
            ].map((service, idx) => (
              <div key={idx} className="bg-black border-l border-[#C9A961] p-6 md:p-8 hover:bg-[#C9A961]/8 transition-all duration-300 cursor-pointer">
                <div className="w-8 h-8 flex items-center justify-center mb-5">
                  <i className={`${service.icon} text-[28px] md:text-[32px] text-[#C9A961]`}></i>
                </div>
                <h3 className="text-[17px] md:text-[20px] text-[#F8F8F8] mb-4 font-medium">{service.title}</h3>
                <p className="text-[13px] md:text-[14px] text-[#B8B8B8] leading-[1.6]">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Elite Property Operations */}
      <section id="why-us" className="bg-[#F8F8F8] py-[80px] md:py-[120px] lg:py-[140px]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px]">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-[5%]">
            <div className="w-full lg:w-[35%]">
              <p className="text-[11px] uppercase tracking-[2px] text-[#C9A961] mb-6">WHY ELITE PROPERTY OPERATIONS™</p>
              <h2 className="text-[28px] md:text-[36px] lg:text-[44px] leading-[1.2] text-black font-light mb-8">
                Operational Discipline. Proven Results.
              </h2>
              <div className="w-20 h-1 bg-[#C9A961]"></div>
            </div>
            <div className="w-full lg:w-[60%]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
                {[
                  'Structured Operational Protocols',
                  'Dedicated Crew Leadership',
                  'Commercial-Grade Equipment',
                  'Quality Assurance Inspections',
                  'Long-Term Asset Preservation Strategy',
                  'Trusted by Property Owners'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <i className="ri-check-line text-[20px] text-[#C9A961] flex-shrink-0 mt-1"></i>
                    <span className="text-[15px] md:text-[17px] text-[#2C2C2C] font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="w-full h-[1px] bg-[#C9A961] mt-16 md:mt-20"></div>
        </div>
      </section>

      {/* Service Regions */}
      <section id="regions" className="bg-[#F8F8F8] py-[80px] md:py-[120px]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px]">
          <h2 className="text-[28px] md:text-[36px] text-black text-center mb-10 md:mb-12 font-light">Service Regions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[
              'West Los Angeles',
              'Beverly Hills',
              'Brentwood',
              'Santa Monica',
              'Marina del Rey',
              'Greater Los Angeles'
            ].map((region, idx) => (
              <div key={idx} className="bg-white border border-[#2C2C2C] rounded-sm p-5 md:p-6 text-center text-[16px] md:text-[18px] text-black hover:border-l-4 hover:border-l-[#C9A961] transition-all duration-300 cursor-pointer">
                {region}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-[#C9A961] py-14 md:py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-[60px] lg:px-[120px]">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-10 md:mb-12">
            <div className="w-full md:w-[40%]">
              <p className="text-[18px] md:text-[20px] text-[#F8F8F8] font-light mb-4">Elite Property Operations™</p>
              <p className="text-[13px] md:text-[14px] text-[#B8B8B8] italic">Structured Precision. Elevated Standards.</p>
            </div>
            <div className="w-full md:w-[30%]">
              <div className="space-y-4">
                <a href="tel:+1234567890" className="block text-[14px] md:text-[15px] text-[#F8F8F8] hover:text-[#C9A961] hover:underline transition-colors cursor-pointer">
                  (310) 721-2271
                </a>
                <a href="mailto:info@docgod.com" className="block text-[14px] md:text-[15px] text-[#F8F8F8] hover:text-[#C9A961] hover:underline transition-colors cursor-pointer">
                  info@docgod.com
                </a>
              </div>
            </div>
            <div className="w-full md:w-[30%] md:flex md:flex-col md:items-end gap-4">
              <Link to="/contact" className="flex items-center justify-center cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/b8716fa8a6d7792031f552f74d12f7bc/83934bc45ac34654febf39565a6f5f5c.png"
                  alt="Elite Property Operations"
                  className="h-16 w-auto"
                />
              </Link>
            </div>
          </div>
          <div className="text-center pt-10 md:pt-12 border-t border-[#2C2C2C]">
            <p className="text-[11px] md:text-[12px] text-[#6B6B6B]">
              © 2026 Elite Property Operations™. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
